import { useMemo, useState } from 'react';

type InboxItem = {
  id: string;
  type: '写真' | 'PDF' | '発注' | 'メモ';
  title: string;
  hint: string;
};

const initialInboxItems: InboxItem[] = [
  { id: 'inbox-001', type: '写真', title: '防火扉まわりの写真 4枚', hint: '未紐付け / 新宿センタービル？' },
  { id: 'inbox-002', type: 'PDF', title: '空調設備の点検報告書', hint: '未紐付け / 南青山タワー？' },
  { id: 'inbox-003', type: '発注', title: '照明交換の見積PDF', hint: '未紐付け / 業者: 光電工業' },
];

const initialLinkedItems: InboxItem[] = [
  { id: 'linked-001', type: 'PDF', title: '消防設備点検報告書', hint: '赤坂テラス / 法定点検に紐付け済み' },
];

export function BuildingMaintenanceDashboardPage() {
  const [inboxItems, setInboxItems] = useState<InboxItem[]>(initialInboxItems);
  const [linkedItems, setLinkedItems] = useState<InboxItem[]>(initialLinkedItems);

  const inboxCount = inboxItems.length;
  const linkedCount = linkedItems.length;

  const assignNext = () => {
    if (inboxItems.length === 0) return;
    const [next, ...rest] = inboxItems;
    setInboxItems(rest);
    setLinkedItems([next, ...linkedItems]);
  };

  const kpis = useMemo(
    () => [
      { label: '管理ビル数', value: '12棟', note: '運用中 9 / 準備中 3' },
      { label: '今月の法定点検', value: '18件', note: '未完了 6件' },
      { label: '見積・発注', value: '7件', note: '見積待ち 3件' },
      { label: '写真・書類', value: '128件', note: '今週追加 15件' },
    ],
    []
  );

  return (
    <div className="bm2-grid" style={{ gap: '22px' }}>
      <section>
        <h2 className="bm2-section-title" data-testid="bm-dashboard-title">全体ダッシュボード</h2>
        <div className="bm2-grid cols-3">
          {kpis.map((item) => (
            <div key={item.label} className="bm2-card">
              <small>{item.label}</small>
              <div className="bm2-kpi">
                <strong>{item.value}</strong>
                <span className="bm2-pill">最新</span>
              </div>
              <div className="bm2-highlight">{item.note}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bm2-split">
        <div className="bm2-card">
          <h3>受信トレイ（未整理）</h3>
          <p className="bm2-highlight">
            まず受け取る → 後で整理。ビル/法定点検/発注に紐付ける前提の一時保管。
          </p>
          <div className="bm2-grid cols-3" style={{ marginTop: '12px' }}>
            <div className="bm2-card" style={{ boxShadow: 'none' }}>
              <small>未整理（受信）</small>
              <div className="bm2-kpi">
                <strong data-testid="bm-inbox-count">{inboxCount}</strong>
                <span className="bm2-pill warn">要整理</span>
              </div>
            </div>
            <div className="bm2-card" style={{ boxShadow: 'none' }}>
              <small>整理済み</small>
              <div className="bm2-kpi">
                <strong data-testid="bm-linked-count">{linkedCount}</strong>
                <span className="bm2-pill ok">紐付け済み</span>
              </div>
            </div>
            <div className="bm2-card" style={{ boxShadow: 'none' }}>
              <small>ルール</small>
              <div className="bm2-highlight">業者名/ビル名が入れば自動候補</div>
            </div>
          </div>
          <ul className="bm2-list">
            {inboxItems.map((item) => (
              <li key={item.id} data-testid={`bm-inbox-item-${item.id}`}>
                <div>
                  <div>{item.title}</div>
                  <small>{item.hint}</small>
                </div>
                <span className="bm2-tag">{item.type}</span>
              </li>
            ))}
            {inboxItems.length === 0 && (
              <li>
                <div>未整理データはありません</div>
                <span className="bm2-tag">完了</span>
              </li>
            )}
          </ul>
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            <button
              type="button"
              className="bm2-button primary"
              onClick={assignNext}
              data-testid="bm-inbox-assign"
            >
              1件紐付け
            </button>
            <button type="button" className="bm2-button ghost">
              一括整理ルールを設定
            </button>
          </div>
        </div>
        <div className="bm2-grid" style={{ gap: '16px' }}>
          <div className="bm2-card">
            <h3>業者稼働状況</h3>
            <ul className="bm2-list">
              <li>
                <div>三星サービス</div>
                <span className="bm2-pill">点検中</span>
              </li>
              <li>
                <div>光電工業</div>
                <span className="bm2-pill warn">見積待ち</span>
              </li>
              <li>
                <div>東都設備</div>
                <span className="bm2-pill ok">完了報告</span>
              </li>
            </ul>
          </div>
          <div className="bm2-card">
            <h3>見積・発注管理</h3>
            <p className="bm2-highlight">今週の発注 3件 / 依頼中 4件</p>
            <ul className="bm2-list">
              <li>
                <div>照明交換 / 南青山タワー</div>
                <span className="bm2-tag">見積PDF</span>
              </li>
              <li>
                <div>空調点検 / 赤坂テラス</div>
                <span className="bm2-tag">承認待ち</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bm2-card">
        <h3>写真・書類プレビュー</h3>
        <div className="bm2-photo-grid">
          <div className="bm2-photo-card">
            <strong>写真 4枚</strong>
            <span>消防設備点検 / 赤坂テラス</span>
            <span className="bm2-tag">プレビュー</span>
          </div>
          <div className="bm2-photo-card">
            <strong>PDF 1件</strong>
            <span>空調設備報告書 / 南青山タワー</span>
            <span className="bm2-tag">閲覧可能</span>
          </div>
          <div className="bm2-photo-card">
            <strong>写真 6枚</strong>
            <span>共用部巡回 / 神田パーク</span>
            <span className="bm2-tag">共有</span>
          </div>
        </div>
      </section>
    </div>
  );
}
